import { ArrowLeft, Copy, ExternalLink, MessageCircle, Phone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../../../assets/scss/pages/FeedbackPage.module.scss';
import api from '../../../config/axios-conf';
import { useAuthStore } from '../../../stores/authStore';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';

export default function FeedbackQrDetail() {
    const { billId } = useParams();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);

    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState('');

    const shopId = user?.shopId;
    const feedbackUrl = `https://feedback.sotiem.vn/feedback?billId=${billId}&shopId=${shopId}`;

    const fetchBill = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/bills/${billId}`);
            setBill(data.bill || data);
        } catch (err) {
            console.error('Error fetching bill:', err);
        } finally {
            setLoading(false);
        }
    }, [billId]);

    useEffect(() => {
        fetchBill();
    }, [fetchBill]);

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        setCopied(label);
        setTimeout(() => setCopied(''), 1500);
    };

    const customerPhone = bill?.customer?.phone || '';
    const customerName = bill?.customer?.name || 'Khách hàng';

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className={styles.title} style={{ fontSize: 20, marginBottom: 2 }}>
                            QR đánh giá dịch vụ
                        </h1>
                        <p className={styles.subtitle}>Hoá đơn #{bill?.code || bill?.id || billId}</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 16,
                            padding: 32,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 20,
                            minWidth: 300,
                        }}
                    >
                        <div className={styles.skeleton} style={{ width: 224, height: 224, borderRadius: 12 }} />
                        <div className={styles.skeleton} style={{ width: 200, height: 16, borderRadius: 6 }} />
                        <div className={styles.skeleton} style={{ width: 160, height: 14, borderRadius: 6 }} />
                        <div className={styles.skeleton} style={{ width: '100%', height: 40, borderRadius: 10 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 280 }}>
                        <div
                            style={{
                                background: '#fff',
                                borderRadius: 16,
                                padding: 24,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                                marginBottom: 20,
                            }}
                        >
                            <div
                                className={styles.skeleton}
                                style={{ width: 100, height: 20, borderRadius: 6, marginBottom: 16 }}
                            />
                            {[1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}
                                >
                                    <div
                                        className={styles.skeleton}
                                        style={{ width: 80, height: 14, borderRadius: 6 }}
                                    />
                                    <div
                                        className={styles.skeleton}
                                        style={{ width: 120, height: 14, borderRadius: 6 }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div
                            style={{
                                background: '#fff',
                                borderRadius: 16,
                                padding: 24,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                            }}
                        >
                            <div
                                className={styles.skeleton}
                                style={{ width: 80, height: 20, borderRadius: 6, marginBottom: 16 }}
                            />
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className={styles.skeleton}
                                    style={{ width: '100%', height: 46, borderRadius: 10, marginBottom: 10 }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* QR Code */}
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 16,
                            padding: 32,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 20,
                            minWidth: 300,
                        }}
                    >
                        <div
                            style={{
                                border: '2px solid #0098F6',
                                borderRadius: 12,
                                padding: 12,
                                background: '#fff',
                            }}
                        >
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(feedbackUrl)}`}
                                alt="QR Code"
                                style={{ width: 200, height: 200, display: 'block' }}
                            />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', marginBottom: 6 }}>
                                Quét mã QR để đánh giá dịch vụ
                            </div>
                            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5, maxWidth: 280 }}>
                                Khách hàng vui lòng dùng camera hoặc Zalo để quét mã này và gửi đánh giá về hệ thống.
                            </div>
                        </div>

                        {/* Copy link */}
                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '10px 20px',
                                border: '1.5px solid #ECEEF2',
                                borderRadius: 10,
                                background: '#fff',
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#1A1A1A',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                width: '100%',
                                justifyContent: 'center',
                            }}
                            onClick={() => handleCopy(feedbackUrl, 'link')}
                        >
                            <Copy size={14} />
                            {copied === 'link' ? 'Đã copy!' : 'Copy link đánh giá'}
                        </button>
                    </div>

                    {/* Info + Actions */}
                    <div style={{ flex: 1, minWidth: 280 }}>
                        {/* Bill info */}
                        <div
                            style={{
                                background: '#fff',
                                borderRadius: 16,
                                padding: 24,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                                marginBottom: 20,
                            }}
                        >
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', marginBottom: 16 }}>
                                Thông tin
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                    <span style={{ color: '#6B7280' }}>Mã hoá đơn</span>
                                    <span style={{ fontWeight: 500, color: '#000' }}>
                                        #{bill?.code || bill?.id || billId}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                    <span style={{ color: '#6B7280' }}>Khách hàng</span>
                                    <span style={{ fontWeight: 500, color: '#000' }}>{customerName}</span>
                                </div>
                                {customerPhone && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: 14,
                                        }}
                                    >
                                        <span style={{ color: '#6B7280' }}>SĐT</span>
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                fontWeight: 500,
                                                color: '#000',
                                            }}
                                        >
                                            {formatPhoneNumber(customerPhone)}
                                            <button
                                                style={{
                                                    border: 'none',
                                                    background: 'none',
                                                    cursor: 'pointer',
                                                    color: '#6B7280',
                                                    padding: 2,
                                                }}
                                                onClick={() => handleCopy(customerPhone, 'phone')}
                                            >
                                                <Copy size={13} />
                                            </button>
                                            {copied === 'phone' && (
                                                <span style={{ fontSize: 11, color: '#22C55E' }}>Đã copy</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {bill?.total != null && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                        <span style={{ color: '#6B7280' }}>Tổng tiền</span>
                                        <span style={{ fontWeight: 600, color: '#0098F6' }}>
                                            {formatCurrency(bill.total)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mẫu tin nhắn */}
                        <div
                            style={{
                                background: '#fff',
                                borderRadius: 16,
                                padding: 24,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                                marginBottom: 20,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12,
                                }}
                            >
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>Mẫu tin nhắn</div>
                                <button
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '6px 14px',
                                        border: '1.5px solid #ECEEF2',
                                        borderRadius: 8,
                                        background: '#fff',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#0098F6',
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                    }}
                                    onClick={() =>
                                        handleCopy(
                                            `Chào ${customerName},\nCảm ơn bạn đã ghé thăm cửa hàng hôm nay 💛\n\nMình luôn mong muốn mang đến trải nghiệm ngày càng tốt hơn, nên nếu bạn có thể dành 1 phút để chia sẻ cảm nhận, chúng mình sẽ rất trân trọng:\n${feedbackUrl}\n\nMỗi đánh giá của bạn là động lực để chúng mình cải thiện và phục vụ bạn tốt hơn trong những lần sau ✨\n\nCảm ơn bạn thật nhiều! ❤️`,
                                            'msg',
                                        )
                                    }
                                >
                                    <Copy size={13} />
                                    {copied === 'msg' ? 'Đã copy!' : 'Copy'}
                                </button>
                            </div>
                            <div
                                style={{
                                    background: '#FAFBFC',
                                    borderRadius: 10,
                                    padding: 16,
                                    fontSize: 14,
                                    color: '#1A1A1A',
                                    lineHeight: 1.7,
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                {`Chào ${customerName},\nCảm ơn bạn đã ghé thăm cửa hàng hôm nay 💛\n\nChúng mình luôn mong muốn mang đến trải nghiệm ngày càng tốt hơn, nên nếu bạn có thể dành `}
                                <strong>1 phút để chia sẻ cảm nhận</strong>
                                {`, chúng mình sẽ rất trân trọng:\n`}
                                <span style={{ color: '#0098F6', wordBreak: 'break-all' }}>{feedbackUrl}</span>
                                {`\n\nMỗi đánh giá của bạn là động lực để chúng mình cải thiện và phục vụ bạn tốt hơn trong những lần sau ✨\n\nCảm ơn bạn thật nhiều! ❤️`}
                            </div>
                        </div>

                        {/* Share actions */}
                        <div
                            style={{
                                background: '#fff',
                                borderRadius: 16,
                                padding: 24,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                            }}
                        >
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A', marginBottom: 16 }}>
                                Chia sẻ
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {customerPhone && (
                                    <a
                                        href={`https://zalo.me/${customerPhone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '12px 16px',
                                            border: '1.5px solid #ECEEF2',
                                            borderRadius: 10,
                                            textDecoration: 'none',
                                            color: '#1A1A1A',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#0068FF';
                                            e.currentTarget.style.background = '#F0F6FF';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#ECEEF2';
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <MessageCircle size={18} style={{ color: '#0068FF' }} />
                                        Nhắn Zalo cho {customerName}
                                        <ExternalLink size={14} style={{ marginLeft: 'auto', color: '#6B7280' }} />
                                    </a>
                                )}
                                {customerPhone && (
                                    <a
                                        href={`tel:${customerPhone}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '12px 16px',
                                            border: '1.5px solid #ECEEF2',
                                            borderRadius: 10,
                                            textDecoration: 'none',
                                            color: '#1A1A1A',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#22C55E';
                                            e.currentTarget.style.background = '#F3FEF7';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#ECEEF2';
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <Phone size={18} style={{ color: '#22C55E' }} />
                                        Gọi {formatPhoneNumber(customerPhone)}
                                        <ExternalLink size={14} style={{ marginLeft: 'auto', color: '#6B7280' }} />
                                    </a>
                                )}
                                <button
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '12px 16px',
                                        border: '1.5px solid #ECEEF2',
                                        borderRadius: 10,
                                        background: 'none',
                                        color: '#1A1A1A',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                        transition: 'all 0.15s',
                                        width: '100%',
                                    }}
                                    onClick={() => handleCopy(feedbackUrl, 'share')}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#0098F6';
                                        e.currentTarget.style.background = '#E6F4FF';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#ECEEF2';
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    <Copy size={18} style={{ color: '#0098F6' }} />
                                    {copied === 'share' ? 'Đã copy link!' : 'Copy link chia sẻ'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
